import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { loginUser } from '../../services/authService';
import './DonateLogin.css';

const DonateLogin: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if user was redirected from a protected route
  const fromPath = (location.state as any)?.from || null;

  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [generalError, setGeneralError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [showError, setShowError] = useState(true);
  const [rememberMe, setRememberMe] = useState(false);

  // Load saved email on component mount
  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    if (savedEmail) {
      setFormData(prev => ({ ...prev, email: savedEmail }));
      setRememberMe(true);
    }
  }, []);

  const handleInputChange = (field: string, value: string) => {
    setFormData({ ...formData, [field]: value });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
    // Clear general error when user starts typing
    if (generalError) {
      setGeneralError('');
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (!formData.password.trim()) {
      newErrors.password = 'Password is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setErrors({});
    setGeneralError('');
    setShowError(true);

    try {
      const result = await loginUser(formData.email, formData.password);

      if (result.success && result.user && result.userData) {
        console.log('Login successful:', result.userData);
        setLoginSuccess(true);

        const userId = result.user.uid;

        // Handle Remember Me
        if (rememberMe) {
          localStorage.setItem('rememberedEmail', formData.email);
        } else {
          localStorage.removeItem('rememberedEmail');
        }

        // Redirect after 1.5 seconds
        setTimeout(() => {
          // If user came from a specific path, redirect there; otherwise go to dashboard with user ID
          // Map old /donate path to /donate-anonymously
          let redirectPath = fromPath || `/dashboard?id=${userId}`;
          if (redirectPath === '/donate') {
            redirectPath = '/donate-anonymously';
          }
          // If redirecting to dashboard, ensure it has the ID parameter
          if (redirectPath === '/dashboard') {
            redirectPath = `/dashboard?id=${userId}`;
          }
          navigate(redirectPath, { replace: true });
        }, 1500);
      } else {
        // Show professional error message
        const errorMessage = result.error || 'Unable to sign in. Please verify your credentials and try again.';
        setGeneralError(errorMessage);
        // Also mark fields with error state for visual feedback
        setErrors({
          email: ' ',
          password: ' '
        });
      }
    } catch (error: any) {
      console.error('Login error:', error);
      const errorMessage = error.message || 'An unexpected error occurred. Please try again.';
      setGeneralError(errorMessage);
      // Also mark fields with error state for visual feedback
      setErrors({
        email: ' ',
        password: ' '
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="donate-page">
      <section className="donate-section">
        <div className="donate-content">
          {/* Left Section - Image with Overlay */}
          <div className="donate-image-section">
            <div className="donate-image-container">
              <img
                src="/images/donate/donate.jpg"
                alt="Donation"
                className="donate-image"
              />
              <div className="donate-image-overlay">
                <div className="donate-welcome-text">
                  <div className="welcome-line">Login & Donate</div>
                  <div className="welcome-title">Welcome Back</div>
                  <div className="welcome-subtitle">
                    Sign in to access your donation history, manage recurring donations, and track your impact.
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Section - Form Card */}
          <div className="donate-form-section">
            <div className="donate-form-card">
              <div className="form-card-label">DONOR LOGIN</div>
              <h1 className="form-card-title">Sign In</h1>
              <p className="form-card-subtitle">
                Access your dashboard and continue making a difference
              </p>

              {loginSuccess ? (
                <div className="login-success-message">
                  <div className="success-icon">✓</div>
                  <h2>Login Successful!</h2>
                  <p>Redirecting to your dashboard...</p>
                </div>
              ) : (
                <form className="donate-form" onSubmit={handleSubmit}>
                  {/* General Error Message */}
                  {generalError && showError && (
                    <div className="general-error-message">
                      <div className="error-icon-container">
                        <svg className="error-icon-svg" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M10 6V10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M10 14H10.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                      <div className="error-content">
                        <div className="error-title">Authentication Failed</div>
                        <div className="error-text">{generalError}</div>
                      </div>
                      <button
                        type="button"
                        className="error-dismiss-btn"
                        onClick={() => setShowError(false)}
                        aria-label="Dismiss error message"
                      >
                        <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>
                    </div>
                  )}

                  {/* Email Field */}
                  <div className="form-group">
                    <label>
                      Email Address
                      <span className="required-asterisk">*</span>
                    </label>
                    <input
                      type="email"
                      placeholder="Enter your email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className={errors.email ? 'error' : ''}
                      disabled={isLoading}
                    />
                    {errors.email && errors.email.trim() && (
                      <div className="error-message">{errors.email}</div>
                    )}
                  </div>

                  {/* Password Field */}
                  <div className="form-group">
                    <label>
                      Password
                      <span className="required-asterisk">*</span>
                    </label>
                    <div className="password-input-wrapper">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your password"
                        value={formData.password}
                        onChange={(e) => handleInputChange('password', e.target.value)}
                        className={errors.password ? 'error' : ''}
                        disabled={isLoading}
                      />
                      <button
                        type="button"
                        className="password-toggle-btn"
                        onClick={() => setShowPassword(!showPassword)}
                        tabIndex={-1}
                      >
                        {showPassword ? '👁️' : '👁️‍🗨️'}
                      </button>
                    </div>
                    {errors.password && errors.password.trim() && (
                      <div className="error-message">{errors.password}</div>
                    )}
                  </div>

                  {/* Remember Me & Forgot Password Row */}
                  <div className="remember-forgot-row">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        disabled={isLoading}
                        className="custom-checkbox"
                      />
                      <span className="checkbox-custom"></span>
                      <span className="checkbox-text">Remember me</span>
                    </label>

                    <Link to="#" onClick={(e) => { e.preventDefault(); alert('Password reset functionality coming soon!'); }} className="forgot-link">
                      Forgot password?
                    </Link>
                  </div>

                  {/* Submit Button */}
                  <div className="form-actions">
                    <button
                      type="submit"
                      className="btn-next"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <span className="spinner"></span>
                          Signing in...
                        </>
                      ) : (
                        'Sign In →'
                      )}
                    </button>
                  </div>

                  {/* Register Link */}
                  <div className="auth-switch-link">
                    <p>
                      Don't have an account?
                      <Link to="/register"> Register here</Link>
                    </p>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DonateLogin;
